import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { IconCurrencyDollar } from '@tabler/icons-react';
import { Badge } from '../../../ui/badge';
import debounce from '../../../../utils/debounce';

/**
 * Payment Options Panel Component
 * Shows three separate payment types as alternatives, not a sum
 */
const PricingPanel = ({
  defaultPayment,
  qrtPayment,
  shortformPayment,
  onPricingUpdate
}) => {
  const [localValues, setLocalValues] = useState({
    defaultPayment: defaultPayment || 0,
    qrtPayment: qrtPayment || 0,
    shortformPayment: shortformPayment || 0
  });

  // Update local values when props change
  useEffect(() => {
    setLocalValues({
      defaultPayment: defaultPayment || 0,
      qrtPayment: qrtPayment || 0,
      shortformPayment: shortformPayment || 0
    });
  }, [defaultPayment, qrtPayment, shortformPayment]);

  // Get the highest payment rate for display
  const highestPayment = useMemo(() => {
    return Math.max(
      parseFloat(localValues.defaultPayment) || 0,
      parseFloat(localValues.qrtPayment) || 0,
      parseFloat(localValues.shortformPayment) || 0
    );
  }, [localValues]);

  // Debounced update function
  const debounceUpdate = useCallback(
    debounce((field, value) => {
      onPricingUpdate({ [field]: parseFloat(value) || 0 });
    }, 500),
    [onPricingUpdate]
  );

  // Handle input changes
  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setLocalValues(prev => ({
        ...prev,
        [field]: value
      }));
      debounceUpdate(field, value);
    }
  };

  // Get payment rate category for display
  const getPaymentCategory = (amount) => {
    if (amount === 0) return 'Not Set';
    if (amount < 50) return 'Low';
    if (amount < 100) return 'Medium';
    if (amount < 200) return 'High';
    return 'Premium';
  };

  const getPaymentBadgeVariant = (amount) => {
    if (amount === 0) return 'secondary';
    if (amount < 50) return 'warning';
    if (amount < 100) return 'default';
    if (amount < 200) return 'success';
    return 'info';
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center mb-2">
        <IconCurrencyDollar size={16} className="mr-1 text-accent" />
        <h6 className="text-base font-semibold text-text-primary">
          Payment Rates
        </h6>
      </div>

      {/* Payment Options */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-xs text-text-secondary mb-1">Default</label>
          <div className="flex items-center glass-input px-2 py-1.5">
            <span className="text-xs text-text-tertiary mr-1">$</span>
            <input
              type="text"
              value={localValues.defaultPayment}
              onChange={handleInputChange('defaultPayment')}
              className="w-full bg-transparent text-sm text-text-primary focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">QRT</label>
          <div className="flex items-center glass-input px-2 py-1.5">
            <span className="text-xs text-text-tertiary mr-1">$</span>
            <input
              type="text"
              value={localValues.qrtPayment}
              onChange={handleInputChange('qrtPayment')}
              className="w-full bg-transparent text-sm text-text-primary focus:outline-none"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs text-text-secondary mb-1">Short</label>
          <div className="flex items-center glass-input px-2 py-1.5">
            <span className="text-xs text-text-tertiary mr-1">$</span>
            <input
              type="text"
              value={localValues.shortformPayment}
              onChange={handleInputChange('shortformPayment')}
              className="w-full bg-transparent text-sm text-text-primary focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Overall Payment Level Indicator */}
      <div className="flex justify-center mt-2">
        <Badge variant={getPaymentBadgeVariant(highestPayment)} className="text-[0.7rem] h-5">
          {getPaymentCategory(highestPayment)}
        </Badge>
      </div>
    </div>
  );
};

export default PricingPanel;
